import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../utils/AuthContext';
import {
  DEFAULT_PAYMENT_LAG_SETTINGS,
  addBusinessDays,
  type PaymentLagSettings,
} from '../../utils/paymentDueFlags';
import {
  savePaymentLagSettings,
  subscribePaymentLagSettings,
} from '../../utils/paymentTermsSettings';

const ROW_META: Array<{
  key: keyof PaymentLagSettings;
  label: string;
  anchor: string;
}> = [
  { key: 'depositLagBusinessDays', label: '50% deposit', anchor: 'after contract signing' },
  {
    key: 'preShipmentLagBusinessDays',
    label: '40% pre-shipment',
    anchor: 'after production completes (last Production task / milestone)',
  },
  {
    key: 'finalLagBusinessDays',
    label: '10% final',
    anchor: 'after installation or delivery completes',
  },
];

function previewDate(lagDays: number): string {
  return addBusinessDays(new Date(), lagDays).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Payment timing defaults — business-day lag between each installment's anchor
 * event and its expected payment date. Drives Gantt payment flags, the Ready
 * to Bill queue, and the cash flow projection.
 */
const PaymentTermsSettingsCard: React.FC = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<PaymentLagSettings>({
    ...DEFAULT_PAYMENT_LAG_SETTINGS,
  });
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saved' | 'error'>('idle');

  useEffect(() => {
    return subscribePaymentLagSettings(loaded => {
      // Don't clobber in-progress edits with the snapshot echo.
      if (!dirtyRef.current) setSettings(loaded);
    });
  }, []);

  const setLag = (key: keyof PaymentLagSettings, raw: string) => {
    const n = Math.min(90, Math.max(0, Math.round(Number(raw) || 0)));
    setSettings(prev => ({ ...prev, [key]: n }));
    dirtyRef.current = true;
    setDirty(true);
    setStatus('idle');
  };

  const save = async () => {
    if (!user?.id) return;
    setSaving(true);
    setStatus('idle');
    try {
      await savePaymentLagSettings(settings, { userId: user.id, userName: user.name || user.email });
      dirtyRef.current = false;
      setDirty(false);
      setStatus('saved');
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      console.error('Error saving payment terms:', err);
      setStatus('error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="settings-ops-section" aria-label="Client Payment Timing">
      <h4 className="settings-ops-section-title">Client Payment Timing</h4>
      <p className="setting-description">
        How many business days after each anchor event a client payment is expected. These lags
        shift the payment flags on the Gantt, the Ready to Bill due dates, and the cash flow
        projection on Finances.
      </p>

      {ROW_META.map(row => (
        <div
          key={row.key}
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            flexWrap: 'wrap',
            padding: '14px 16px',
            background: 'var(--color-surface)',
            borderRadius: 8,
            marginBottom: 8,
            border: '1px solid var(--color-border)',
          }}
        >
          <div style={{ flex: 1, minWidth: 180 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-text-primary)' }}>{row.label}</span>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>{row.anchor}</div>
          </div>
          <div style={{ width: 130 }}>
            <label
              style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 4 }}
              htmlFor={`payment-lag-${row.key}`}
            >
              Business days
            </label>
            <input
              id={`payment-lag-${row.key}`}
              type="number"
              min={0}
              max={90}
              value={settings[row.key]}
              onChange={e => setLag(row.key, e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 14 }}
            />
          </div>
          <div style={{ width: 190, fontSize: 12, color: 'var(--color-text-secondary)' }}>
            If the anchor were today: <strong style={{ color: 'var(--color-text-primary)' }}>{previewDate(settings[row.key])}</strong>
          </div>
        </div>
      ))}

      <div style={{ display: 'flex', gap: 10, marginTop: 16, alignItems: 'center' }}>
        <button className="save-button" disabled={saving || !dirty} onClick={() => void save()}>
          {saving ? 'Saving...' : status === 'saved' ? '\u2713 Saved' : 'Save Payment Timing'}
        </button>
        {status === 'error' && (
          <span style={{ fontSize: 13, color: 'var(--color-error-text)' }}>Could not save — try again.</span>
        )}
      </div>
    </section>
  );
};

export default PaymentTermsSettingsCard;
